// Sub-modales de pregunta al soltar archivos en el modal de correo:
//  - PreguntaImagenModal: imagen <=10MB → ¿inline en el cuerpo o adjunto?
//    (Joseph eligió "preguntar al soltar" en el plan G3.)
//  - PreguntaOversizeModal: archivo >10MB → confirmar subir como evidencia
//    + ZIP cifrado + link en el correo, sin salir del modal.
// Extraídos de EnviarCorreoModal (modularización 2026-06-11).
import Modal from "@/ui/Modal";
import { MAX_ADJUNTO_BYTES, fmtMB } from "@/components/correo/utilsCorreo";

export function PreguntaImagenModal({
  nombre,
  sizeBytes,
  onClose,
  onInline,
  onAdjuntar,
}: {
  nombre: string;
  sizeBytes: number;
  onClose: () => void;
  onInline: () => void;
  onAdjuntar: () => void;
}) {
  return (
    <Modal
      open={true}
      onOpenChange={(v) => !v && onClose()}
      title="¿Cómo agregar la imagen?"
      description={`${nombre} (${fmtMB(sizeBytes)} MB)`}
      size="sm"
    >
      <div className="space-y-2">
        <button
          type="button"
          onClick={onInline}
          className="w-full flex items-start gap-3 rounded border border-ink-700 bg-ink-800 hover:bg-ink-700 px-3 py-2.5 text-left transition"
        >
          <span className="text-xl">🖼️</span>
          <div>
            <div className="text-sm text-zinc-100 font-medium">
              Insertar en el cuerpo del correo
            </div>
            <div className="text-xs text-zinc-400">
              La imagen va dentro del mensaje y aparece donde está el cursor.
              Ideal para capturas de pantalla.
            </div>
          </div>
        </button>
        <button
          type="button"
          onClick={onAdjuntar}
          className="w-full flex items-start gap-3 rounded border border-ink-700 bg-ink-800 hover:bg-ink-700 px-3 py-2.5 text-left transition"
        >
          <span className="text-xl">📎</span>
          <div>
            <div className="text-sm text-zinc-100 font-medium">
              Adjuntar como archivo
            </div>
            <div className="text-xs text-zinc-400">
              La imagen va como archivo adjunto. El destinatario la descarga
              aparte. Ideal cuando quieres conservar la resolución original.
            </div>
          </div>
        </button>
      </div>
    </Modal>
  );
}

export function PreguntaOversizeModal({
  file,
  subiendo,
  onClose,
  onConfirmar,
}: {
  file: File;
  subiendo: boolean;
  onClose: () => void;
  onConfirmar: (f: File) => void;
}) {
  return (
    <Modal
      open={true}
      onOpenChange={(v) => !v && onClose()}
      title="Archivo grande: enviar como enlace seguro"
      description={`${file.name} (${fmtMB(file.size)} MB)`}
      size="sm"
    >
      <div className="space-y-3">
        <div className="text-xs text-zinc-300 leading-relaxed">
          Este archivo pasa los <strong>{fmtMB(MAX_ADJUNTO_BYTES)} MB</strong>{" "}
          de límite por adjunto. Lo guardo en las evidencias del caso, lo
          comprimo en un ZIP protegido con la contraseña del ticket (TE) y
          pongo el enlace de descarga en el correo. Sin salir de esta ventana.
        </div>
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} className="btn-ghost text-sm">
            Cancelar
          </button>
          <button
            type="button"
            onClick={() => onConfirmar(file)}
            disabled={subiendo}
            className="btn-primary text-sm disabled:opacity-50"
          >
            {subiendo ? "Procesando..." : "Subir y generar link"}
          </button>
        </div>
      </div>
    </Modal>
  );
}

export function PreguntaVerInsertarModal({
  nombre,
  onClose,
  onVisualizar,
  onInsertar,
}: {
  nombre: string;
  onClose: () => void;
  onVisualizar: () => void;
  onInsertar: () => void;
}) {
  return (
    <Modal
      open={true}
      onOpenChange={(v) => !v && onClose()}
      title="¿Qué quieres hacer con la imagen?"
      description={nombre}
      size="sm"
    >
      <div className="space-y-2">
        <button
          type="button"
          onClick={onVisualizar}
          className="w-full flex items-start gap-3 rounded border border-ink-700 bg-ink-800 hover:bg-ink-700 px-3 py-2.5 text-left transition"
        >
          <span className="text-xl">👁️</span>
          <div>
            <div className="text-sm text-zinc-100 font-medium">Visualizar</div>
            <div className="text-xs text-zinc-400">
              Verla a tamaño completo, como en Evidencias, antes de decidir.
            </div>
          </div>
        </button>
        <button
          type="button"
          onClick={onInsertar}
          className="w-full flex items-start gap-3 rounded border border-ink-700 bg-ink-800 hover:bg-ink-700 px-3 py-2.5 text-left transition"
        >
          <span className="text-xl">➕</span>
          <div>
            <div className="text-sm text-zinc-100 font-medium">
              Insertar en el correo
            </div>
            <div className="text-xs text-zinc-400">
              En el cuerpo del mensaje o como archivo adjunto.
            </div>
          </div>
        </button>
      </div>
    </Modal>
  );
}
